import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatAmount, CURRENCY_META } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gameBySlug } from "@/lib/games";

const TABS = [
  { key: "all", label: "All Bets" },
  { key: "high", label: "High Rollers" },
  { key: "mine", label: "My Bets" },
];

const timeAgo = (iso) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const USD_WEIGHT = { FMI: 1, BTC: 1_000_000, ETH: 50_000, USDT: 1, BNB: 600 };

const mask = (name = "") => (name.length <= 4 ? name : `${name.slice(0, 2)}***${name.slice(-2)}`);

export default function LiveFeed() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [bets, setBets] = useState([]);
  const [fresh, setFresh] = useState(new Set());
  const seen = useRef(new Set());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        let data;
        if (tab === "mine") {
          if (!user) { setBets([]); return; }
          ({ data } = await api.get("/bets/me?limit=25"));
        } else {
          ({ data } = await api.get("/bets/live?limit=60"));
          if (tab === "high") data = [...data].sort((a, b) => b.bet * USD_WEIGHT[b.currency] - a.bet * USD_WEIGHT[a.currency]).slice(0, 25);
          else data = data.slice(0, 25);
        }
        if (!alive) return;
        const incoming = new Set(data.filter((b) => !seen.current.has(b.id)).map((b) => b.id));
        data.forEach((b) => seen.current.add(b.id));
        setFresh(incoming);
        setBets(data);
      } catch { /* silent */ }
    };
    load();
    const i = setInterval(load, 4000);
    return () => { alive = false; clearInterval(i); };
  }, [tab, user]);

  return (
    <section className="panel overflow-hidden" data-testid="live-bets">
      <div className="flex items-center gap-1 px-4 pt-3 pb-2 border-b border-white/[0.05]">
        <span className="live-dot mr-2" />
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-4">Live Bets</span>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`live-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === t.key ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white hover:bg-white/5"}`}
            style={{ transition: "background-color 160ms ease, color 160ms ease" }}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="data-table live-table" data-testid="live-bets-table">
          <thead>
            <tr><th>Game</th><th>Player</th><th className="hidden md:table-cell">Time</th><th>Bet</th><th className="hidden sm:table-cell">Multiplier</th><th>Payout</th></tr>
          </thead>
          <tbody>
            {bets.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-zinc-600 py-10">{tab === "mine" && !user ? "Sign in to see your bets." : "Waiting for bets…"}</td></tr>
            ) : bets.map((b) => {
              const g = gameBySlug(b.game);
              const Icon = g?.icon;
              const win = b.profit > 0;
              return (
                <tr key={b.id} className={`${fresh.has(b.id) ? "row-enter" : ""} ${win && b.multiplier >= 10 ? "flash-win" : ""}`} data-testid={`live-row-${b.id}`}>
                  <td>
                    <Link to={`/games/${b.game}`} className="flex items-center gap-2 hover:text-amber-400">
                      {Icon && <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${g.accent}1f`, color: g.accent }}><Icon size={12} /></span>}
                      <span className="font-semibold">{g?.name || b.game}</span>
                    </Link>
                  </td>
                  <td className="text-zinc-400">{tab === "mine" ? b.username : mask(b.username)}</td>
                  <td className="hidden md:table-cell text-zinc-500 font-mono-num text-xs">{timeAgo(b.created_at)}</td>
                  <td className="font-mono-num"><span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: CURRENCY_META[b.currency]?.color }} />{formatAmount(b.bet, b.currency)}</td>
                  <td className={`hidden sm:table-cell font-mono-num ${b.multiplier > 1 ? "text-emerald-400" : "text-zinc-500"}`}>×{Number(b.multiplier || 0).toFixed(2)}</td>
                  <td className={`font-mono-num font-semibold ${win ? "text-emerald-400" : "text-zinc-500"}`}>{win ? "+" : ""}{formatAmount(b.profit, b.currency)} <span className="text-[10px] text-zinc-600">{b.currency}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
